// Every section of the staff app, grouped by how often it's used. The sidebar
// shows them all on laptops; on phones the tab bar shows the main few and
// More lists the rest. Each person only sees what their role lets them use.
import {
	Bank,
	Briefcase,
	Buildings,
	CalendarCheck,
	CalendarDots,
	ChartBar,
	ClipboardText,
	Clock,
	CurrencyGbp,
	Eye,
	FileText,
	Globe,
	House,
	type Icon,
	IdentificationBadge,
	Lifebuoy,
	Lightning,
	ListChecks,
	SealCheck,
	Signature,
	UploadSimple,
	UsersThree,
} from "@phosphor-icons/react";
import type { Me } from "../lib/api.ts";

export type To =
	| "/"
	| "/tasks"
	| "/candidates"
	| "/clients"
	| "/bookings"
	| "/plan"
	| "/timesheets"
	| "/rates"
	| "/reports"
	| "/payroll"
	| "/vacancies"
	| "/compliance"
	| "/settings/documents"
	| "/settings/forms"
	| "/settings/agreements"
	| "/imports"
	| "/settings/access"
	| "/settings/team"
	| "/settings/ways"
	| "/platform"
	| "/platform/feedback";

export interface Section {
	to: To;
	label: string;
	Icon: Icon;
	exact?: boolean;
	permission?: string;
	platformOnly?: boolean;
	count?: "tasks" | "queue";
}

/** A named group of sections. The name shows above it in the sidebar and in More, and on each page above its title. */
export interface Group {
	label: string;
	items: Section[];
}

// Grouped by how often they're used: daily work, money, compliance, then set-up.
const GROUPS: Group[] = [
	{
		label: "Daily work",
		items: [
			{ to: "/", label: "Home", Icon: House, exact: true },
			{ to: "/tasks", label: "Tasks", Icon: ListChecks, permission: "tasks.manage", count: "tasks" },
			{ to: "/candidates", label: "Candidates", Icon: UsersThree },
			{ to: "/clients", label: "Clients", Icon: Buildings },
			{ to: "/bookings", label: "Bookings", Icon: CalendarCheck, permission: "bookings.view" },
			{ to: "/plan", label: "Plan", Icon: CalendarDots, permission: "bookings.view" },
			{ to: "/timesheets", label: "Timesheets", Icon: Clock, permission: "timesheets.view" },
			{ to: "/vacancies", label: "Permanent jobs", Icon: Briefcase, permission: "bookings.view" },
		],
	},
	{
		label: "Money",
		items: [
			{ to: "/rates", label: "Rate cards", Icon: CurrencyGbp, permission: "rate_cards.view" },
			{ to: "/payroll", label: "Payroll", Icon: Bank, permission: "payroll.export" },
			{ to: "/reports", label: "Reports", Icon: ChartBar, permission: "reports.view" },
		],
	},
	{
		label: "Compliance",
		items: [
			{ to: "/compliance", label: "Check documents", Icon: SealCheck, permission: "compliance.verify", count: "queue" },
			{
				to: "/settings/documents",
				label: "Documents you need",
				Icon: FileText,
				permission: "agency.document_types.manage",
			},
			{ to: "/settings/forms", label: "Forms", Icon: ClipboardText, permission: "agency.forms.manage" },
			{ to: "/settings/agreements", label: "Things people sign", Icon: Signature, permission: "agency.forms.manage" },
		],
	},
	{
		label: "Set-up",
		items: [
			{ to: "/imports", label: "Import a spreadsheet", Icon: UploadSimple, permission: "agency.imports.run" },
			{ to: "/settings/team", label: "Your team", Icon: IdentificationBadge, permission: "agency.users.manage" },
			{ to: "/settings/ways", label: "Ways of working", Icon: Lightning, permission: "agency.settings.manage" },
			{ to: "/settings/access", label: "Access and exports", Icon: Eye, permission: "agency.users.manage" },
		],
	},
	{
		label: "SSG",
		items: [
			{ to: "/platform", label: "All agencies", Icon: Globe, platformOnly: true, exact: true },
			{ to: "/platform/feedback", label: "Feedback and problems", Icon: Lifebuoy, platformOnly: true },
		],
	},
];

/** The groups of sections this person can use, empty groups left out. */
export function sectionsFor(me: Me): Group[] {
	const can = (p?: string) => !p || me.permissions.includes(p);
	return GROUPS.map((g) => ({
		...g,
		items: g.items.filter((i) => can(i.permission) && (!i.platformOnly || me.isPlatformAdmin)),
	})).filter((g) => g.items.length);
}
